import type { MaybeRefOrGetter } from 'vue';
import type { CalendarRange } from './build-calendar-range-filter';
import type { CalendarEventSpan } from './calendar-events';
import { Temporal } from 'temporal-polyfill';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUserLocale } from '#layers/base/public';
import { CALENDAR_PAGE_SIZE } from '#layers/listing/constants/calendar';
import { toPlainDate } from './calendar-events';

interface UseCalendarPartialResultsOptions {
	loaded: MaybeRefOrGetter<number>;
	total: MaybeRefOrGetter<number | undefined>;
	isSettled: MaybeRefOrGetter<boolean>;
	/** Tells whether the items query sorted by start; without that order the last loaded start is no cutoff. */
	isSortedByStart: MaybeRefOrGetter<boolean>;
	/** Must keep the items query's order, so the last event is the last one loaded. */
	events: MaybeRefOrGetter<readonly CalendarEventSpan[]>;
	range: MaybeRefOrGetter<CalendarRange>;
}

/** Builds the notice for a loaded page that does not cover the visible range; `notice` is null otherwise. */
export function useCalendarPartialResults({ loaded, total, isSettled, isSortedByStart, events, range }: UseCalendarPartialResultsOptions) {
	const { t } = useI18n();
	const { formatLocale } = useUserLocale();

	const cutoff = computed(() => {
		const loadedCount = toValue(loaded);
		const totalCount = toValue(total);
		const lastEvent = toValue(events).at(-1);

		if (!toValue(isSettled) || !toValue(isSortedByStart) || !lastEvent) return null;
		// Ignores a short page, because it can still carry the previous query's total.
		if (loadedCount < CALENDAR_PAGE_SIZE) return null;
		// Reads a missing total as more items, because the request asks for one row past the page.
		if (totalCount !== undefined && totalCount <= loadedCount) return null;

		const { start } = toValue(range);
		const lastStart = toPlainDate(lastEvent.start);

		// Clamps to the range, because items that start before it can span into it.
		return Temporal.PlainDate.compare(lastStart, start) < 0 ? start : lastStart;
	});

	const notice = computed(() => {
		const date = cutoff.value;
		if (!date) return null;

		const totalCount = toValue(total);
		const named = {
			loaded: toValue(loaded).toLocaleString(formatLocale.value),
			date: date.toLocaleString(formatLocale.value, { month: 'long', day: 'numeric', year: 'numeric' }),
		};

		if (totalCount === undefined) return t('layout.calendar.partial_results.unknown_total', named);
		return t('layout.calendar.partial_results.known_total', { ...named, total: totalCount.toLocaleString(formatLocale.value) });
	});

	return { notice };
}
